import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'

type Severity = 'success' | 'error' | 'warning' | 'info'

interface ToastState {
  open: boolean
  message: string
  severity: Severity
  duration: number
}

const DEFAULT_DURATION: Record<Severity, number> = {
  success: 4000,
  error: 6000,
  warning: 4000,
  info: 4000,
}

interface ToastContextValue {
  toast: {
    success: (message: string) => void
    error: (message: string) => void
    warning: (message: string) => void
    info: (message: string) => void
  }
  showToast: (options: {
    message: string
    severity?: Severity
    duration?: number
  }) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ToastState>({
    open: false,
    message: '',
    severity: 'success',
    duration: 4000,
  })

  const showToast = useCallback(
    ({
      message,
      severity = 'success',
      duration,
    }: {
      message: string
      severity?: Severity
      duration?: number
    }) => {
      setState({
        open: true,
        message,
        severity,
        duration: duration ?? DEFAULT_DURATION[severity],
      })
    },
    [],
  )

  const handleClose = useCallback((_: unknown, reason?: string) => {
    if (reason === 'clickaway') return
    setState((prev) => ({ ...prev, open: false }))
  }, [])

  const toast = useMemo(
    () => ({
      success: (message: string) => showToast({ message, severity: 'success' }),
      error: (message: string) => showToast({ message, severity: 'error' }),
      warning: (message: string) => showToast({ message, severity: 'warning' }),
      info: (message: string) => showToast({ message, severity: 'info' }),
    }),
    [showToast],
  )

  const value = useMemo(() => ({ toast, showToast }), [toast, showToast])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Snackbar
        open={state.open}
        autoHideDuration={state.duration}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={state.severity} variant="filled" onClose={handleClose}>
          {state.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  )
}
